import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { css, styled } from 'styled-components';
import { Button, tk } from '@sunwebgroup/ui-components';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics';

const Wrapper = styled.div``;

const Content = styled.div<{ isExpanded: boolean }>`
  will-change: height;
  transition: height 500ms;
  overflow: hidden;
  position: relative;

  ${({ isExpanded }) =>
    !isExpanded &&
    css`
      &:after {
        bottom: 0;
        content: '';
        display: block;
        left: 0;
        padding-top: ${tk('spacing_4xl')};
        position: absolute;
        width: 100%;
        background-image: linear-gradient(180deg, hsla(0, 0%, 100%, 0), #fff);
      }
    `}
`;

const UnfoldButton = styled(Button)`
  padding-left: 0;
  margin-top: ${tk('spacing_xs')};
`;

enum TransitionStages {
  IDLE = 'IDLE', // Not transitioning, height should be 'auto' so the component is responsive
  TRANSITIONING = 'TRANSITIONING', // Applying the final content height
}

const getElementScrollHeight = (el: HTMLElement | null) => el?.scrollHeight ?? 0;

const getElementHeight = (el: HTMLElement | null) => el?.getBoundingClientRect().height ?? 0;

const isContentOverflowing = (el: HTMLElement | null) => {
  return getElementScrollHeight(el) !== getElementHeight(el);
};

const useReadMoreTransition = (heightShown: number) => {
  const contentRef = React.useRef<HTMLParagraphElement | null>(null);

  const [transitionStage, setTransitionStage] = React.useState(TransitionStages.IDLE);
  const [open, setOpen] = React.useState(false);
  const { sendEvent } = useAnalytics();

  const getCompleteContentStyle = () => {
    if (!open) return { height: heightShown };

    if (transitionStage === TransitionStages.TRANSITIONING) {
      return { height: getElementScrollHeight(contentRef.current) };
    }

    if (transitionStage === TransitionStages.IDLE) {
      return { height: 'auto' };
    }
  };

  const toggleOpen = React.useCallback(() => {
    setOpen(true);
    setTransitionStage(TransitionStages.TRANSITIONING);
    sendEvent(
      createPDPClickEvent({
        element: 'Clickable text',
        description: 'Show more text about acco info',
        result: 'all acco info shown',
        type: 'show more',
      })
    );
  }, [sendEvent]);

  const handleTransitionEnd = React.useCallback(() => setTransitionStage(TransitionStages.IDLE), []);

  React.useLayoutEffect(() => {
    // If the content is not overflowing when the component is mounted for the first time: Move to open without transition
    if (!isContentOverflowing(contentRef.current)) {
      setOpen(true);
      setTransitionStage(TransitionStages.IDLE);
    }
  }, []);

  React.useLayoutEffect(() => {
    if (transitionStage !== TransitionStages.IDLE && !isContentOverflowing(contentRef.current)) {
      // If the short text and the complete text have the same height, the transition is not triggered: reverting back to idle state.
      setTransitionStage(TransitionStages.IDLE);
    }
  }, [transitionStage]);

  return {
    canBeExpanded: !open,
    contentProps: {
      ref: contentRef,
      isExpanded: open,
      onTransitionEnd: handleTransitionEnd,
      style: getCompleteContentStyle(),
    },
    toggleOpen,
  };
};

type ReadMoreProps = PropsWithChildren<{
  heightShown: number;
  readMoreText: string;
}>;

export function ReadMore({ children, heightShown, readMoreText }: ReadMoreProps) {
  const { canBeExpanded, contentProps, toggleOpen } = useReadMoreTransition(heightShown);

  return (
    <Wrapper>
      <Content {...contentProps}>{children}</Content>
      {canBeExpanded && (
        <UnfoldButton onClick={toggleOpen} $hierarchy="tertiary">
          {readMoreText}
        </UnfoldButton>
      )}
    </Wrapper>
  );
}
